import React, { useMemo } from "react";
import {
  PUBLIC_TOP_OFFERS_TABLE_TITLE_WIDTHS,
  PUBLIC_TOP_CREATIVES_TABLE_TITLES,
  PUBLIC_TOP_CREATIVES_TABLE_TITLE_WIDTHS,
} from "src/helpers/Constants";
import { ListType } from "src/helpers/Enums";

const SCORE_HIGH = 75;
const SCORE_MID = 50;

const getScoreColor = (score) => {
  const value = parseFloat(score);
  if (isNaN(value)) return "#8185A8";
  if (value >= SCORE_HIGH) return "#53A852";
  if (value >= SCORE_MID) return "#F0970B";
  return "#DD2E2E";
};

const getChannelIcon = (channel) =>
  channel === ListType.SMS
    ? "/media/icons/chat.svg"
    : "/media/icons/mailPurple.svg";

const PublicOfferRow = ({
  offer,
  rank,
  channel,
  isOpen,
  onToggle,
  formatScore,
}) => {
  const sortedCreatives = useMemo(() => {
    const list = Array.isArray(offer?.creatives) ? offer.creatives : [];
    return [...list].sort((a, b) => (b?.score ?? 0) - (a?.score ?? 0));
  }, [offer?.creatives]);

  const totalCreatives = sortedCreatives.length;
  const scoreColor = getScoreColor(offer?.score);
  const channelLabel = channel === ListType.SMS ? "SMS" : "Email";
  const channelIcon = getChannelIcon(channel);

  return (
    <div
      className={`flex flex-col w-full rounded-lg border bg-white transition-shadow ${
        isOpen
          ? "border-[#7E50EA] shadow-[0_4px_12px_0_rgba(126,80,234,0.08)]"
          : "border-[#E5E7F0]"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center hover:bg-[#FAFAFC] rounded-lg ${
          isOpen ? "rounded-b-none" : ""
        }`}
        style={{ fontFamily: "Inter" }}
      >
        <div
          className="flex items-center h-full py-3 px-[10px]"
          style={{ width: PUBLIC_TOP_OFFERS_TABLE_TITLE_WIDTHS[0] }}
        >
          <div
            className="flex items-center justify-center w-6 h-6 rounded-full bg-[#F2EBFB] text-[#7E50EA] text-[11px] font-bold"
            style={{ fontFamily: "Inter" }}
          >
            {rank}
          </div>
        </div>

        <div
          className="flex items-center h-full py-3 px-[10px] gap-3 min-w-0"
          style={{ width: PUBLIC_TOP_OFFERS_TABLE_TITLE_WIDTHS[1] }}
        >
          <div
            className="w-[3px] h-8 rounded-full flex-shrink-0"
            style={{ backgroundColor: scoreColor }}
          />
          <div className="flex flex-col items-start min-w-0">
            <div
              className="text-[#15151F] text-[13px] font-semibold leading-[16px] truncate max-w-full text-left"
              style={{ fontFamily: "Inter" }}
              title={offer?.name}
            >
              {offer?.name || "-"}
            </div>
            <div
              className="text-[#8185A8] text-[11px] font-medium leading-[14px] flex items-center gap-1"
              style={{ fontFamily: "Inter" }}
            >
              <img alt={channelLabel} src={channelIcon} className="w-3 h-3" />
              {totalCreatives}{" "}
              {totalCreatives === 1 ? "creative" : "creatives"}
            </div>
          </div>
        </div>

        <div
          className="flex items-center h-full py-3 px-[10px]"
          style={{ width: PUBLIC_TOP_OFFERS_TABLE_TITLE_WIDTHS[2] }}
        >
          <div className="flex items-center gap-1">
            <img
              alt="Sends"
              src="/media/icons/rocket-02.svg"
              className="w-[14px] h-[14px]"
            />
            <span
              className="text-[#15151F] text-[12px] font-semibold"
              style={{ fontFamily: "Inter" }}
            >
              {offer?.numberOfSend ?? 0}
            </span>
          </div>
        </div>

        <div
          className="flex items-center h-full py-3 px-[10px]"
          style={{ width: PUBLIC_TOP_OFFERS_TABLE_TITLE_WIDTHS[3] }}
        >
          <span
            className="inline-flex items-center h-[20px] px-[8px] rounded-[34px] text-white text-[11px] font-semibold"
            style={{ fontFamily: "Inter", backgroundColor: scoreColor }}
          >
            {formatScore(offer?.score)}
          </span>
        </div>

        <div
          className="flex items-center justify-center h-full py-3 px-[10px]"
          style={{ width: PUBLIC_TOP_OFFERS_TABLE_TITLE_WIDTHS[4] }}
        >
          <img
            alt="Toggle"
            src={
              isOpen
                ? "/media/icons/chevronUp.svg"
                : "/media/icons/chevronDown.svg"
            }
            className="w-4 h-4"
          />
        </div>
      </button>

      {isOpen && (
        <div className="flex flex-col w-full gap-3 px-6 py-4 border-t border-[#E5E7F0] bg-[#FAFAFC] rounded-b-lg">
          {totalCreatives === 0 ? (
            <div className="flex items-center justify-center py-6">
              <div className="flex flex-col items-center gap-2">
                <img
                  alt="Empty"
                  src="/media/icons/stars-01.svg"
                  className="w-8 h-8"
                />
                <div className="EmptyCaseTitle">No creatives yet</div>
                <div className="EmptyCaseDescription">
                  This offer doesn't have any {channelLabel} creatives to show.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <img
                  alt={channelLabel}
                  src={channelIcon}
                  className="w-4 h-4"
                />
                <div
                  className="text-[#15151F] text-[13px] font-semibold leading-normal"
                  style={{ fontFamily: "Inter" }}
                >
                  {channelLabel} Creatives
                </div>
                <div
                  className="inline-flex items-center h-[18px] px-[6px] rounded-[34px] bg-[#F2EBFB] text-[#7E50EA] text-[11px] font-semibold"
                  style={{ fontFamily: "Inter" }}
                >
                  {totalCreatives}
                </div>
              </div>

              <div className="flex w-full bg-[#F6F6F9] rounded-lg h-9 items-center">
                {PUBLIC_TOP_CREATIVES_TABLE_TITLES.map((title, index) => (
                  <div
                    key={index}
                    className="flex items-center h-full py-2 px-[10px]"
                    style={{
                      width: PUBLIC_TOP_CREATIVES_TABLE_TITLE_WIDTHS[index],
                    }}
                  >
                    <div className="HeaderNameSection">{title}</div>
                  </div>
                ))}
              </div>

              <div className="TableWrapper border-0">
                <table className="Table w-full">
                  <tbody className="flex flex-col gap-2">
                    {sortedCreatives.map((creative, index) => (
                      <tr
                        key={creative?._id ?? index}
                        className="flex rounded-lg border border-[#DBDFE8] bg-white text-[#15151F] text-[12px] font-medium leading-[14px] w-full"
                        style={{ fontFamily: "Inter" }}
                      >
                        <td
                          className="py-3 px-[10px] border-r flex-shrink-0 flex items-center"
                          style={{
                            width:
                              PUBLIC_TOP_CREATIVES_TABLE_TITLE_WIDTHS[0],
                          }}
                        >
                          {creative?.name || "-"}
                        </td>
                        <td
                          className="py-3 px-[10px] border-r flex-shrink-0 flex items-center"
                          style={{
                            width:
                              PUBLIC_TOP_CREATIVES_TABLE_TITLE_WIDTHS[1],
                          }}
                        >
                          {creative?.numberOfSend ?? 0}
                        </td>
                        <td
                          className="py-3 px-[10px] flex-shrink-0 flex items-center"
                          style={{
                            width:
                              PUBLIC_TOP_CREATIVES_TABLE_TITLE_WIDTHS[2],
                          }}
                        >
                          <span
                            className="inline-flex items-center h-[18px] px-[6px] rounded-[34px] text-white text-[11px] font-semibold"
                            style={{
                              fontFamily: "Inter",
                              backgroundColor: getScoreColor(creative?.score),
                            }}
                          >
                            {formatScore(creative?.score)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PublicOfferRow;
